/**
 * Dates written out for the reader, in their language.
 *
 * Common Era dates go through `Intl.DateTimeFormat`, which knows each language's order
 * and month names. `Intl` writes BCE years with the locale's own era word ("BC" in
 * English), so BCE dates are assembled from the widget's templates instead, which say
 * "BCE" — the convention the timeline's input uses — and can be translated.
 */

import { t } from "@/lib/i18n/interpolate";
import type { NumberFormat } from "@/lib/tool-kit/charts/format";
import {
  type HistoricalDate,
  historicalYear,
  type TimeTick,
} from "@/lib/tool-kit/diagrams/dates";

export interface EraLabels {
  /** "{year} BCE" */
  yearBce: string;
  /** "{year} CE" */
  yearCe: string;
  /** "c. {date}" */
  circa: string;
  /** "{month} {year}" — a month in a BCE year. */
  monthYear: string;
  /** "{monthDay}, {year}" — a day in a BCE year. */
  dayMonthYear: string;
}

/** A UTC `Date` for any year — `Date.UTC` would read years 0–99 as 1900–1999. */
export function utcDate(year: number, month: number, day: number): Date {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  return date;
}

export function monthName(
  locale: string,
  month: number,
  width: "long" | "short" | "narrow"
): string {
  return new Intl.DateTimeFormat(locale, {
    month: width,
    timeZone: "UTC",
  }).format(utcDate(2000, month, 1));
}

/**
 * A year as historians write it: `44 BCE`; `79 CE` when the timeline also holds BCE
 * dates (`showEra`), plain `1969` otherwise. Years of five digits and more are grouped.
 */
export function formatYear(
  astronomical: number,
  showEra: boolean,
  labels: EraLabels,
  fmt: NumberFormat
): string {
  const { year, bce } = historicalYear(astronomical);
  const written = fmt(year, { useGrouping: year >= 10_000 });
  if (bce) {
    return t(labels.yearBce, { year: written });
  }
  return showEra ? t(labels.yearCe, { year: written }) : written;
}

function formatCommonEra(date: HistoricalDate, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: date.precision === "day" ? "numeric" : undefined,
    timeZone: "UTC",
  }).format(utcDate(date.year, date.month, date.day));
}

/** A timeline date at the precision it was written: `1066`, `July 1969`, `15 March, 44 BCE`. */
export function formatHistoricalDate(
  date: HistoricalDate,
  showEra: boolean,
  locale: string,
  labels: EraLabels,
  fmt: NumberFormat
): string {
  const year = formatYear(date.year, showEra, labels, fmt);
  let text = year;
  if (date.precision !== "year" && (date.bce || showEra)) {
    const month = monthName(locale, date.month, "long");
    text =
      date.precision === "month"
        ? t(labels.monthYear, { month, year })
        : t(labels.dayMonthYear, {
            monthDay: new Intl.DateTimeFormat(locale, {
              month: "long",
              day: "numeric",
              timeZone: "UTC",
            }).format(utcDate(2000, date.month, date.day)),
            year,
          });
  } else if (date.precision !== "year") {
    text = formatCommonEra(date, locale);
  }
  return date.circa ? t(labels.circa, { date: text }) : text;
}

/** A tick under a time axis: a year; a short month (with its year in January and on the
 * first tick); or a day and short month. */
export function formatTick(
  tick: TimeTick,
  index: number,
  showEra: boolean,
  locale: string,
  labels: EraLabels,
  fmt: NumberFormat
): string {
  const { year, month, day } = tick.date;
  if (tick.unit === "year") {
    return formatYear(year, showEra, labels, fmt);
  }
  if (tick.unit === "month") {
    const name = monthName(locale, month, "short");
    if (month !== 1 && index !== 0) {
      return name;
    }
    return t(labels.monthYear, {
      month: name,
      year: formatYear(year, showEra, labels, fmt),
    });
  }
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(utcDate(2000, month, day));
}
